import { Button, Card, Grid, Menu, MenuItem, Paper, Typography } from '@material-ui/core';
import React, { useState } from 'react';

import {
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { useDashboardCardStyles } from '~/src/dash/views/ias-publisher/IASDashboard/common/consts';
import {
  IIASDashboardMetricsCard,
  IIASDashboardSelectableCardMetricInfo,
} from '~/src/dash/views/ias-publisher/IASDashboard/common/types';

const getCompareTextClass = (
  isPercentChangeNegative: boolean,
  isInverseMetric: boolean,
  classes: Record<string, string>,
) => {
  if (isInverseMetric) {
    return isPercentChangeNegative
      ? classes.dashboardCardComparePositive
      : classes.dashboardCardCompareNegative;
  } else {
    return isPercentChangeNegative
      ? classes.dashboardCardCompareNegative
      : classes.dashboardCardComparePositive;
  }
};

const getValueTextClass = (
  loadStatus: number,
  isActive: boolean,
  classes: Record<string, string>,
) => {
  if (loadStatus !== 1) {
    if (isActive) {
      return classes.dashboardCardValueSmallWhite;
    } else {
      return classes.dashboardCardValueSmallDark;
    }
  } else {
    if (isActive) {
      return classes.dashboardCardValueWhite;
    } else {
      return classes.dashboardCardValueDark;
    }
  }
};

const ComparisonArrow = ({ isPercentChangeNegative, isInverseMetric }: Record<string, boolean>) => {
  switch (true) {
    case isPercentChangeNegative && !isInverseMetric:
      return <img src='/assets/shared/svg/RedArrow.svg' style={{ transform: 'rotate(180deg)' }} />;
    case isPercentChangeNegative && isInverseMetric:
      return <img src='/assets/shared/svg/Arrow.svg' style={{ transform: 'rotate(180deg)' }} />;
    case !isPercentChangeNegative && !isInverseMetric:
      return <img src='/assets/shared/svg/Arrow.svg' />;
    default:
      return <img src='/assets/shared/svg/RedArrow.svg' />;
  }
};

const displayValue = (value: string, loadStatus: number) => {
  switch (loadStatus) {
    case LoadingStateLoading:
      return 'Loading...';
    case LoadingStateHasData:
      return value === 'NaN%' || value === 'NaN' ? 'N/A' : value;
    case LoadingStateNoData:
      return 'No Data';
    default:
      return 'N/A';
  }
};

interface IIASDashboardSelectableCardProps {
  color: string;
  isComparedToEnabled?: boolean;
  isInverseMetric?: boolean;
  isPercentChangeNegative?: boolean;
  label: string;
  percentChange?: string;
  value: string;
  isActive: boolean;
  handleClickSelectableCard: (dataKey: string, index: number) => void;
  handleSwitchCardSelected: (newkey: string, index: number) => void;
  loadStatus: number;
  dataKey: string;
  cards: IIASDashboardMetricsCard[];
  cardIndex: number;
  allMetrics: IIASDashboardSelectableCardMetricInfo[];
}

export const IASDashboardSelectableCard = ({
  color,
  isComparedToEnabled = false,
  isInverseMetric = false,
  isPercentChangeNegative = false,
  label,
  percentChange,
  value,
  isActive,
  handleClickSelectableCard,
  handleSwitchCardSelected,
  loadStatus,
  dataKey,
  cards,
  cardIndex,
  allMetrics,
}: IIASDashboardSelectableCardProps) => {
  const classes = useDashboardCardStyles();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(Boolean(event.currentTarget));
    event.stopPropagation();
  };

  const handleClose = (event: React.MouseEvent<HTMLLIElement>) => {
    setAnchorEl(null);
    setOpen(false);
    event.stopPropagation();
  };

  return (
    <Grid item>
      <Paper
        className={classes.paperCards}
        style={{
          backgroundColor: isActive ? color : 'white',
          cursor: 'pointer',
        }}
        onClick={() => {
          handleClickSelectableCard(dataKey, cardIndex);
          setOpen(Boolean(anchorEl));
        }}
      >
        {/* CARD LABEL */}
        <Grid
          style={{
            background: isActive ? color : 'white',
            height: 32,
          }}
        >
          <Card
            className={classes.dashboardTitleCard}
            style={{ backgroundColor: isActive ? color : 'white' }}
          >
            {isActive ? (
              <Button
                style={{
                  justifyContent: 'left',
                  textTransform: 'capitalize',
                  maxWidth: '100%',
                  maxHeight: '100%',
                  minWidth: '100%',
                  minHeight: '100%',
                  marginTop: 2,
                }}
                id='basic-button'
                aria-controls={open ? 'basic-menu' : undefined}
                aria-haspopup='true'
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}
              >
                <Typography
                  style={{
                    fontWeight: 500,
                    fontSize: 14,
                    color: '#FFFFFF',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    wordWrap: 'break-word',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {label}
                </Typography>
              </Button>
            ) : (
              <Grid
                style={{
                  background: 'white',
                  paddingTop: 8,
                  paddingLeft: 8,
                  paddingRight: 8,
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                <Typography className={classes.dashboardCardTitleDark}>{label}</Typography>
              </Grid>
            )}
            <Menu
              id='basic-menu'
              elevation={0}
              getContentAnchorEl={null}
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              keepMounted
              open={open}
              onClose={handleClose}
              MenuListProps={{
                'aria-labelledby': 'basic-button',
              }}
            >
              {allMetrics.map((metric) => {
                return cards.find((card) => card.dataKey === metric.dataKey) ? (
                  <MenuItem
                    style={{
                      cursor: 'default',
                      color: '#a2a2a2',
                    }}
                    value={metric.label}
                    onClick={(event) => {
                      handleClose(event);
                    }}
                  >
                    {metric.label}
                  </MenuItem>
                ) : (
                  <MenuItem
                    value={metric.label}
                    onClick={(event) => {
                      handleSwitchCardSelected(metric.key, cardIndex);
                      handleClose(event);
                    }}
                  >
                    {metric.label}
                  </MenuItem>
                );
              })}
            </Menu>
          </Card>
        </Grid>
        {/* CARD VALUE */}
        <Grid
          container
          direction='column'
          alignItems='flex-start'
          style={{
            justifyContent: 'flex-end',
            height: '100%',
            width: '100%',
            backgroundColor: isActive ? color : 'white',
          }}
        >
          <Typography className={getValueTextClass(loadStatus, isActive, classes)}>
            {/* getting NaN because no existing data */}
            {displayValue(value, loadStatus)}
          </Typography>
        </Grid>
        {/* CARD CHANGE VALUE */}
        {isComparedToEnabled && loadStatus === LoadingStateHasData && (
          <Grid
            container
            direction='row'
            alignItems='center'
            style={{
              height: '100%',
              width: '100%',
              paddingLeft: 12,
              backgroundColor: '#FFFFFF',
              borderTop: '0.5px solid lightgrey',
              borderBottomLeftRadius: 2,
              borderBottomRightRadius: 2,
            }}
          >
            <Grid item style={{ padding: '0px 0px 2px 0px' }}>
              <ComparisonArrow
                isPercentChangeNegative={isPercentChangeNegative}
                isInverseMetric={isInverseMetric}
              />
            </Grid>
            <Grid item>
              <Typography
                className={getCompareTextClass(isPercentChangeNegative, isInverseMetric, classes)}
              >
                {/* getting NaN because no existing data */}
                {percentChange === 'NaN%' ? 'N/A' : percentChange}
              </Typography>
            </Grid>
            <Grid item>
              <Typography className={classes.dashboardCardText}>/ previous period</Typography>
            </Grid>
          </Grid>
        )}
      </Paper>
    </Grid>
  );
};
